import axios from "axios";
import { makeUseAxios } from "axios-hooks";

import { useLiveReload } from "@thalamos/frontend";

import { config } from "../../config/index.js";
import { redirectToSignIn } from "../../routing/redirectToSignIn.js";
import { rememberServerVersion } from "./serverVersion.js";
import { checkVersionMismatch } from "./versionCheck.js";

export const apiAxios = axios.create({
  baseURL: config.backendUrl,

  // A parameter given more than one value is sent as a repeated key —
  // `type=a&type=b` — which is what tsoa reads an array query parameter from.
  // Axios would otherwise bracket them, `type[]=a`, and tsoa sees no `type` at
  // all: the request fails validation rather than arriving with fewer values,
  // so getting this wrong is silent at the call site and total at the server.
  paramsSerializer: { indexes: null },

  // The session lives in a cookie scoped to the parent domain, so every call
  // must be credentialed.
  withCredentials: true,

  // 4xx resolves onto `response.data` rather than throwing; 5xx still rejects.
  validateStatus: (status) => status >= 200 && status < 500,
});

/**
 * Because `validateStatus` above admits any status < 500, a 4xx error body
 * resolves onto `response.data` rather than throwing an AxiosError. Callers that
 * branch on `.status` therefore need `data` typed as one of two shapes. Spell the
 * pair out at each call site via `ResponseBody<Success, Failure>` — not behind a
 * single alias — so type hints show both arms. It is a plain union, not a
 * discriminated Result: the arms are distinguished by HTTP status, which axios
 * cannot bind to `data` in the type system.
 */
export type ResponseBody<Success, Failure> = Success | Failure;

/**
 * A serialised backend error, as it arrives on `response.data` on the failure
 * path: `message` from a BaseError, `reason` from an explicit TsoaResponse, and
 * `message` + `details` from a TSOA `ValidateError` (a 400 validation failure).
 * `details` is TSOA's field-errors map, which no frontend caller reads, so it is
 * left as `unknown` rather than pulling in the TSOA type.
 */
export type ErrorBody = {
  message?: string;
  reason?: string;
  details?: unknown;
};

/**
 * The success shape for endpoints that return 204 with no body. It is
 * `Record<string, never>` (not `undefined`/`void`) so a diagnostic field reads
 * off a `ResponseBody` union without narrowing; the empty object encodes "no
 * content" and is not the wire value (a 204 body is the empty string).
 */
export type NoContentBody = Record<string, never>;

// An expired or absent session is not a per-caller concern: handling it here is
// what lets a page assume it has a user rather than branching on status itself.
//
// The 401 is never handed on. Leaving for sign-in does not unload the page at
// once, and a caller given the response in the meantime renders it as a
// failure, flashing an error before sign-in appears. Left pending, the caller
// never sees it, and whatever it shows while waiting stays until the browser
// leaves.
apiAxios.interceptors.response.use((response) => {
  if (response.status === 401) {
    redirectToSignIn();
    return new Promise<never>(() => {});
  }

  return response;
});

// A deploy replaces the bundle the browser is running. Record the version the
// server reports, and reload when it differs — but only on pages where that
// cannot lose work.
apiAxios.interceptors.response.use((response) => {
  // axios types every header as `any`, so narrow before handing it on.
  const header: unknown = response.headers["x-code-version"];
  const serverVersion = typeof header === "string" ? header : undefined;
  rememberServerVersion(serverVersion);
  checkVersionMismatch(serverVersion);
  return response;
});

export const useApiAxios = makeUseAxios({ axios: apiAxios, cache: false });

/**
 * The channels a read depends on: either a fixed list, or a function of what
 * the read returned.
 *
 * The function form is what lets a read depend on records its own response
 * names — a young person's record answers from a whole merge tree, and the ids
 * in that tree are not derivable from the request. Called with `undefined`
 * before the first response, which is when a caller should name whatever it can
 * work out from the request alone.
 */
export type LiveChannels<TResponse> =
  | readonly string[]
  | undefined
  | ((data: TResponse | undefined) => readonly string[] | undefined);

/**
 * A read that re-runs itself when the server says something it depends on
 * changed.
 *
 * The tuple is returned unchanged, so `refetch` keeps axios-hooks' own identity
 * and is safe in a dependency array. Debouncing is the registry's — see
 * `debounceMs` on `SubscriptionRegistryOptions`.
 *
 * A skipped read holds no subscription and makes no call. `manual` says the
 * caller does not want this data yet, which is different from wanting it
 * quietly, so nothing is watched until it is wanted.
 */
export function useLiveApiAxios<TResponse>(
  channels: LiveChannels<TResponse>,
  requestConfig: Parameters<typeof useApiAxios<TResponse>>[0],
  options?: Parameters<typeof useApiAxios<TResponse>>[1],
): ReturnType<typeof useApiAxios<TResponse>> {
  const result = useApiAxios<TResponse>(requestConfig, options);
  const refetch = result[1];

  const watched =
    typeof channels === "function" ? channels(result[0].data) : channels;

  useLiveReload(options?.manual === true ? undefined : watched, () => {
    void refetch();
  });

  return result;
}
